import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ScanLine, Check, X, AlertTriangle, Clock, FileText, ChevronLeft, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';

interface OCRJob {
  id: number;
  status: string;
  file_name: string;
  created_at: string;
  extracted_json: string;
  confidence_json: string;
}

const statusColors: Record<string, string> = {
  pending: 'bg-tangerine-100 text-tangerine-700 dark:bg-tangerine-500/20 dark:text-tangerine-300',
  processing: 'bg-electric-100 text-electric-700 dark:bg-electric-500/20 dark:text-electric-300',
  completed: 'bg-lime-100 text-lime-700 dark:bg-lime-500/20 dark:text-lime-300',
  failed: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300',
  reviewed: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300',
};

export default function OCRReviewPage() {
  const { jobId } = useParams();

  const { data, isLoading } = useQuery({
    queryKey: ['ocr-jobs'],
    queryFn: async () => api.get<any>('/api/ocr/jobs'),
  });

  const jobs: OCRJob[] = data?.data || [];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3">
          {jobId && (
            <Button variant="ghost" size="icon" className="h-10 w-10" asChild>
              <Link to="/ocr-review">
                <ChevronLeft className="w-5 h-5" />
              </Link>
            </Button>
          )}
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
              <ScanLine className="w-5 h-5 sm:w-6 sm:h-6 text-magenta-400" />
              OCR Review
            </h1>
            <p className="text-xs sm:text-sm text-light-text-muted mt-1">Review AI-extracted admission forms</p>
          </div>
        </div>
      </div>

      {!jobId ? (
        /* Job List */
        <div className="space-y-3 sm:space-y-4">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="glass-card p-3 sm:p-5 rounded-2xl animate-pulse h-20 sm:h-24" />
            ))
          ) : jobs.length === 0 ? (
            <div className="text-center py-16">
              <ScanLine className="w-12 h-12 sm:w-16 sm:h-16 text-light-text-muted mx-auto mb-4" />
              <h3 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text mb-2">No OCR Jobs</h3>
              <p className="text-xs sm:text-sm text-light-text-muted mb-4">Upload an admission form to get started</p>
              <Button asChild>
                <Link to="/admissions/upload" className="inline-flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Upload Form
                </Link>
              </Button>
            </div>
          ) : (
            jobs.map((job: OCRJob, index: number) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="glass-card p-3 sm:p-5 rounded-2xl hover:shadow-lg transition-all"
              >
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      job.status === 'completed' ? 'gradient-lime' :
                      job.status === 'failed' ? 'gradient-rose' :
                      job.status === 'processing' ? 'gradient-electric' :
                      'gradient-tangerine'
                    }`}>
                      {job.status === 'completed' ? <Check className="w-5 h-5 sm:w-6 sm:h-6 text-white" /> :
                       job.status === 'failed' ? <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" /> :
                       job.status === 'processing' ? <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white" /> :
                       <ScanLine className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-xs sm:text-sm font-semibold text-light-text dark:text-dark-text truncate">{job.file_name}</h3>
                      <p className="text-[10px] sm:text-xs text-light-text-muted">{new Date(job.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    <span className={`text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full ${statusColors[job.status]}`}>
                      {job.status}
                    </span>
                    {job.status === 'completed' && (
                      <Button asChild variant="outline" className="h-8 text-xs sm:text-sm">
                        <Link to={`/ocr-review/${job.id}`} className="flex items-center gap-1">
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">Review</span>
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      ) : (
        /* Job Detail Review */
        <OCRJobDetail jobId={parseInt(jobId)} />
      )}
    </div>
  );
}

function OCRJobDetail({ jobId }: { jobId: number }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['ocr-job', jobId],
    queryFn: async () => api.get<any>(`/api/ocr/jobs/${jobId}`),
  });

  const approveMutation = useMutation({
    mutationFn: async () => api.post(`/api/ocr/jobs/${jobId}/approve`, {}),
    onSuccess: () => {
      toast.success('Form approved and student created');
      queryClient.invalidateQueries({ queryKey: ['ocr-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to approve');
    },
  });

  const job = data?.data;
  let extracted: Record<string, any> = {};
  try {
    extracted = JSON.parse(job?.extracted_json || '{}');
  } catch {
    extracted = {};
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
      {/* Image Preview */}
      <div className="glass-card p-3 sm:p-4 rounded-2xl">
        <h3 className="text-base sm:text-lg font-semibold text-light-text dark:text-dark-text mb-4">Original Form</h3>
        <div className="aspect-[3/4] bg-light-elevated dark:bg-dark-elevated rounded-xl flex items-center justify-center">
          <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-light-text-muted" />
        </div>
      </div>

      {/* Extracted Data */}
      <div className="space-y-3 sm:space-y-4">
        <div className="glass-card p-3 sm:p-4 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-light-text dark:text-dark-text">Extracted Fields</h3>
            <span className="text-xs px-2 py-1 rounded-full bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-300">
              Confidence: 85%
            </span>
          </div>

          <div className="space-y-2 sm:space-y-3">
            {Object.entries(extracted).length > 0 ? (
              Object.entries(extracted).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between p-2 sm:p-3 rounded-xl bg-light-elevated dark:bg-dark-elevated">
                  <span className="text-xs sm:text-sm text-light-text-muted capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="text-xs sm:text-sm font-medium text-light-text dark:text-dark-text">
                    {typeof value === 'string' ? value : JSON.stringify(value)}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-4 sm:py-8">
                <AlertTriangle className="w-10 h-10 sm:w-12 sm:h-12 text-tangerine-500 mx-auto mb-3" />
                <p className="text-light-text-muted">No extracted data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={() => approveMutation.mutate()}
            disabled={approveMutation.isPending}
            className="flex-1 h-10 flex items-center justify-center gap-2"
          >
            {approveMutation.isPending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Approve & Create
          </Button>
          <Button variant="outline" className="flex-1 h-10 flex items-center justify-center gap-2">
            <X className="w-4 h-4" />
            Discard
          </Button>
        </div>
      </div>
    </div>
  );
}
